import React, { useState, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CameraSetList from './CameraSetList';
import CameraSetForm from './CameraSetForm';
import { useCameraSets, useAvailableCameras } from '../../hooks/useCameraSets';

function CameraSetManager({ open, onClose, onSetsChanged }) {
  const { sets, loading, error, refetch, create, update, remove } = useCameraSets();
  const { cameras: availableCameras, loading: camerasLoading, refetch: refetchCameras } = useAvailableCameras();

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [editingSet, setEditingSet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Notification state
  const [notification, setNotification] = useState(null);

  const showNotification = (message, severity = 'success') => {
    setNotification({ message, severity });
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  // Create handlers
  const handleOpenCreate = () => {
    setEditingSet(null);
    setSaveError(null);
    setFormOpen(true);
  };

  // Edit handlers
  const handleEdit = (set) => {
    setEditingSet(set);
    setSaveError(null);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingSet(null);
    setSaveError(null);
  };

  const handleSave = async (data) => {
    try {
      setSaving(true);
      setSaveError(null);

      if (editingSet) {
        await update(editingSet.id, data);
        showNotification('Camera set updated successfully');
      } else {
        await create(data);
        showNotification('Camera set created successfully');
      }

      handleFormClose();
      onSetsChanged?.();
    } catch (err) {
      console.error('[CANVMS/CameraSetManager] Error saving set:', err);
      setSaveError(err.response?.data?.message || err.message || 'Failed to save camera set');
    } finally {
      setSaving(false);
    }
  };

  // Delete handler
  const handleDelete = async (setId) => {
    try {
      await remove(setId);
      showNotification('Camera set deleted successfully');
      onSetsChanged?.();
    } catch (err) {
      console.error('[CANVMS/CameraSetManager] Error deleting set:', err);
      showNotification(
        err.response?.data?.message || err.message || 'Failed to delete camera set',
        'error'
      );
    }
  };

  const handleRefresh = useCallback(() => {
    refetch();
    refetchCameras();
  }, [refetch, refetchCameras]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: '100%', md: 700 }, maxWidth: '100%' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ flex: 1 }}>
              Camera Sets
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
            >
              Add Set
            </Button>
            <IconButton onClick={onClose} sx={{ ml: 1 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <CameraSetList
              sets={sets}
              loading={loading}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </Box>
        </Box>
      </Drawer>

      {/* Create/Edit Form Dialog */}
      <CameraSetForm
        open={formOpen}
        onClose={handleFormClose}
        onSave={handleSave}
        set={editingSet}
        availableCameras={availableCameras}
        camerasLoading={camerasLoading}
        saving={saving}
        error={saveError}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notification && (
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}

export default CameraSetManager;
